import { values } from 'lodash';
import { object, string, mixed, array } from 'yup';

import { FILE_SIZE, SOLR_VERSIONS, SUPPORTED_FORMATS } from './constants';
import { INSTANCE_CATEGORY, INSTANCE_PLANS } from '../../../constants';
import i18next from '../../../i18n';
import { formatBytes, sentenceCase } from '../../../utils/utils';

const solrVersionValues = SOLR_VERSIONS.map((version) => version.value);

export const instanceSchema = () => ({
  plan: string()
    .oneOf(
      values([INSTANCE_CATEGORY.COMMUNITY, ...Object.values(INSTANCE_PLANS)])
    )
    .required(),
  solrVersion: string().oneOf(solrVersionValues).required(),
  name: string()
    .required(
      sentenceCase(
        i18next.t('inputRequired', {
          ns: 'validations',
          field: i18next.t('instanceName', { ns: 'fields' }),
        })
      )
    )
    .min(2, i18next.t('minLength', { ns: 'validations', min: '2' }))
    .max(32, i18next.t('maxLength', { ns: 'validations', max: '32' }))
    .matches(
      /^[ぁ-んァ-ン一-龯ーA-Za-z0-9][ぁ-んァ-ン一-龯ーA-Za-z0-9_-\s]*[ぁ-んァ-ン一-龯ーA-Za-z0-9]$/,
      i18next.t('nameFormat', { ns: 'validations' })
    ),
  description: string().max(
    256,
    i18next.t('maxLength', { ns: 'validations', max: '256' })
  ),
  icon: mixed()
    .test(
      'fileFormat',
      i18next.t('invalidImageFormat', { ns: 'validations' }),
      (value) => !value || (value && SUPPORTED_FORMATS.includes(value.type))
    )
    .test(
      'fileSize',
      i18next.t('invalidSize', {
        ns: 'validations',
        size: formatBytes(FILE_SIZE.BYTES, 0),
      }),
      (value) => !value || (value && value.size <= FILE_SIZE.BYTES)
    ),
  subdomain: string()
    .required(
      sentenceCase(
        i18next.t('inputRequired', {
          ns: 'validations',
          field: i18next.t('instanceSubdomain', { ns: 'fields' }),
        })
      )
    )
    .max(64, i18next.t('maxLength', { ns: 'validations', max: '64' }))
    .matches(
      /^[0-9a-z][0-9a-z-]*[0-9a-z]$/,
      i18next.t('invalidSubdomain', { ns: 'validations' })
    ),
  cidrs: array(),
});

export const schema = (checkDuplicate) =>
  object({
    type: instanceSchema().type,
    plan: instanceSchema().plan,
    solrVersion: instanceSchema().solrVersion,
    icon: instanceSchema().icon,
    name: instanceSchema().name,
    description: instanceSchema().description,
    subdomain: instanceSchema().subdomain.test(
      'isUnique',
      sentenceCase(
        i18next.t('duplicate', {
          ns: 'validations',
          field: i18next.t('instanceSubdomain', { ns: 'fields' }),
        })
      ),
      async (value) => {
        if (!value) return true;
        const { isValid } = await checkDuplicate(value);
        return isValid;
      }
    ),
    cidrs: instanceSchema().ipAddresses,
    storageSize: instanceSchema().storageSize,
  });
